import React, { useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { ScheduledQueryBackHeader } from './ScheduledQueryBackHeader'
import '../Results/CrossClientResults.scss'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { useGetCrossClientQueryExecutionQuery } from '../../../store/crossClient/crossClient'
import { useCreateScheduledCrossClientQueryMutation } from '../../../store/crossClient/scheduledCrossClient'
import { Spinner } from '../../../components/Spinner/Spinner'
import { NotAuthorizedPageContent } from '../../../components/NotAuthorizedPageContent'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { ScheduledQueryForm } from './ScheduledQueryForm'
import { ScheduledQueryDetailsCard } from './ScheduledQueryDetailsCard'
import { ScheduledQuerySuccess } from './ScheduledQuerySuccess'
import { DEFAULT_FREQUENCY, frequencyToCronExpressions, type ScheduledQueryFrequency } from './scheduledQueryFrequency'
import { DEFAULT_SCHEDULED_AUTOMATION, type ScheduledCrossClientQueryAutomation } from '../../../models/ScheduledCrossClientQuery'
import './ScheduledQuery.scss'

export const ScheduleQueryView: React.FC = () => {
  const { executionId } = useParams()

  if (!executionId) {
    return <div>No query provided</div>
  }

  // Key by execution so all form state resets when navigating between executions
  return <ScheduleQueryContent key={executionId} executionId={executionId} />
}

interface ScheduleQueryContentProps {
  executionId: string
}

const ScheduleQueryContent: React.FC<ScheduleQueryContentProps> = ({ executionId }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { portalRef } = useContext(HeaderContext)

  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const { hasScope } = useZocksAuth()
  const canSchedule = hasScope('**', 'schd_ccq:create', ['schd_ccq:admin'])

  const {
    data: execution,
    isLoading: executionLoading,
    isError: executionError,
  } = useGetCrossClientQueryExecutionQuery({ accountId, executionId }, { skip: !canSchedule })

  const [createScheduledQuery, { isLoading: creating }] = useCreateScheduledCrossClientQueryMutation()

  const [name, setName] = useState('')
  const [runAsUserId, setRunAsUserId] = useState(currentUser.id)
  const [frequency, setFrequency] = useState<ScheduledQueryFrequency>(DEFAULT_FREQUENCY)
  const [automation, setAutomation] = useState<ScheduledCrossClientQueryAutomation>(DEFAULT_SCHEDULED_AUTOMATION)
  const [scheduled, setScheduled] = useState(false)
  const prefilledRef = useRef(false)

  useEffect(() => {
    if (!execution || prefilledRef.current) {
      return
    }
    prefilledRef.current = true
    if (!name) {
      setName(execution.userQuestion)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prefill once when the execution arrives
  }, [execution])

  if (!canSchedule) {
    return <NotAuthorizedPageContent />
  }

  if (executionLoading) {
    return (
      <div className='d-flex flex-column align-items-center justify-content-center gap-3 mt-10'>
        <Spinner />
      </div>
    )
  }

  if (executionError) {
    return <div className='paragraph-medium zocks-text-gray-700 p-4'>Failed to load the query. Please try again.</div>
  }

  if (!execution) {
    return <div className='paragraph-medium zocks-text-gray-700 p-4'>Query not found</div>
  }

  const goBackToQuery = () => navigate(`/cross-client/executions/${executionId}`)

  const handleSubmit = async () => {
    try {
      await createScheduledQuery({
        accountId,
        request: {
          name: name.trim(),
          schedule: { cronExpressions: frequencyToCronExpressions(frequency) },
          definition: {
            type: 'fromExecution',
            sourceExecutionId: executionId,
          },
          automation,
          ...(runAsUserId !== currentUser.id ? { userId: runAsUserId } : {}),
        },
      }).unwrap()
      setScheduled(true)
    } catch (e) {
      Sentry.captureException(e)
      dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to schedule the query. Please try again.'] }))
    }
  }

  return (
    <div className='truly-full-size-content d-flex cross-client-results-container'>
      {portalRef && portalRef.current ? createPortal(<ScheduledQueryBackHeader to={`/cross-client/executions/${executionId}`} />, portalRef.current) : null}
      {scheduled ? (
        <div className='flex-grow-1'>
          <ScheduledQuerySuccess frequency={frequency} onGoBack={goBackToQuery} />
        </div>
      ) : (
        <div className='d-flex align-items-start zocks-p-2.0 scheduled-query-layout'>
          <div className='d-flex flex-column zocks-gap-2 scheduled-query-main'>
            <div className='d-flex flex-column gap-1'>
              <h3 className='mb-0'>Schedule query</h3>
              <span className='paragraph-medium zocks-text-gray-700'>Run this query on a recurring schedule to regularly find clients that qualify.</span>
            </div>
            <ScheduledQueryForm
              name={name}
              onNameChange={setName}
              runAsUserId={runAsUserId}
              onRunAsUserIdChange={setRunAsUserId}
              frequency={frequency}
              onFrequencyChange={setFrequency}
              automation={automation}
              onAutomationChange={setAutomation}
              submitLabel='Schedule query'
              submitting={creating}
              onSubmit={handleSubmit}
              onCancel={goBackToQuery}
            />
          </div>
          <div className='scheduled-query-rail'>
            <ScheduledQueryDetailsCard details={execution} />
          </div>
        </div>
      )}
    </div>
  )
}
